/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

type WithUnknownFields = Record<string, unknown>;

type AdjustmentLike = {
    price?: number;
    itemText?: string;
    calloutMsg?: string;
    priceAdjustmentId?: string;
    promotionId?: string;
    c_isRecyclingFee?: unknown;
};

type RemainingExplicitFee = {
    cents: number;
};

/**
 * Fee-amount custom fields observed in current SFN basket / line-item payloads.
 *
 * Keep this list limited to numeric fee totals. Recycling-related booleans and
 * workflow fields (e.g. opt-in / eligibility) are intentionally excluded.
 */
const RECYCLING_FEE_KEYS = ['c_stateRecyclingFee', 'c_recyclingFee', 'c_mattressRecyclingFee'] as const;
const CURRENCY_MINOR_UNIT_FACTOR = 100;

function toMinorUnits(amount: number): number {
    return Math.round(amount * CURRENCY_MINOR_UNIT_FACTOR);
}

function readPositiveCustomFee(entity: unknown): number | undefined {
    if (!entity || typeof entity !== 'object') return undefined;

    const candidateEntity = entity as WithUnknownFields;
    let total = 0;
    for (const key of RECYCLING_FEE_KEYS) {
        const value = candidateEntity[key];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            total += value;
        }
    }

    return total > 0 ? total : undefined;
}

export function getLineItemRecyclingFeeTotal(lineItem: unknown): number | undefined {
    return readPositiveCustomFee(lineItem);
}

export function isRecyclingAdjustmentByLabel(adjustment: AdjustmentLike): boolean {
    const amount = adjustment.price;
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return false;

    // Prefer stable/non-localized indicators when available.
    if (adjustment.c_isRecyclingFee === true) return true;

    const priceAdjustmentId =
        typeof adjustment.priceAdjustmentId === 'string' ? adjustment.priceAdjustmentId.toLowerCase() : '';
    const promotionId = typeof adjustment.promotionId === 'string' ? adjustment.promotionId.toLowerCase() : '';
    if (priceAdjustmentId.includes('recycl') || promotionId.includes('recycl')) return true;

    // Last-resort fallback for legacy payloads without structured flags/IDs.
    const itemText = typeof adjustment.itemText === 'string' ? adjustment.itemText : '';
    const calloutMsg = typeof adjustment.calloutMsg === 'string' ? adjustment.calloutMsg : '';
    return `${itemText} ${calloutMsg}`.toLowerCase().includes('recycl');
}

export function sumLabelBasedRecyclingAdjustments(adjustments: AdjustmentLike[] = []): number {
    return adjustments.reduce((sum, adjustment) => {
        if (!isRecyclingAdjustmentByLabel(adjustment)) return sum;
        return sum + (adjustment.price ?? 0);
    }, 0);
}

export function shouldHideAsRecyclingAdjustment(
    adjustment: AdjustmentLike,
    remainingExplicitFeeAccumulator: RemainingExplicitFee
): boolean {
    const amount = adjustment.price;
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return false;
    if (!isRecyclingAdjustmentByLabel(adjustment)) return false;
    const amountCents = toMinorUnits(amount);

    // This function mutates the shared accumulator so callers can consume a
    // fixed explicit recycling-fee budget across multiple adjustments.
    // Prefer a stable custom-field amount when present; this avoids coupling
    // line-item rendering to mutable label text.
    if (remainingExplicitFeeAccumulator.cents > 0 && amountCents <= remainingExplicitFeeAccumulator.cents) {
        remainingExplicitFeeAccumulator.cents = Math.max(0, remainingExplicitFeeAccumulator.cents - amountCents);
        return true;
    }

    return true;
}

type ProductItemWithAdjustments = {
    priceAdjustments?: AdjustmentLike[];
};

type BasketWithItems = {
    productItems?: ProductItemWithAdjustments[];
};

/**
 * Resolves a basket-level recycling fee total using a deterministic precedence:
 *
 * 1) Basket-level custom fee fields (source of truth when present)
 * 2) Sum of line-item custom fee fields
 * 3) Sum of label-detected recycling price adjustments (legacy fallback)
 *
 * We intentionally prefer basket-level fee fields first, even when line-item
 * derived totals differ, because basket custom fields are the authoritative
 * aggregate emitted by backend integrations for this basket snapshot.
 */
export function getBasketRecyclingFeeTotal(basket: unknown): number | undefined {
    const basketCustomFee = readPositiveCustomFee(basket);
    if (basketCustomFee) return basketCustomFee;

    const rawProductItems = (basket as BasketWithItems | undefined)?.productItems;
    const productItems = Array.isArray(rawProductItems) ? rawProductItems : [];
    const lineItemCustomFeeTotal = productItems.reduce(
        (sum, item) => sum + (getLineItemRecyclingFeeTotal(item) ?? 0),
        0
    );
    if (lineItemCustomFeeTotal > 0) return lineItemCustomFeeTotal;

    const adjustmentFeeTotal = productItems.reduce(
        (sum, item) => sum + sumLabelBasedRecyclingAdjustments(item.priceAdjustments ?? []),
        0
    );

    return adjustmentFeeTotal > 0 ? adjustmentFeeTotal : undefined;
}

export function getRecyclingAdjustmentTotal(lineItem: ProductItemWithAdjustments): number {
    const customFee = getLineItemRecyclingFeeTotal(lineItem);
    if (customFee) return customFee;

    return sumLabelBasedRecyclingAdjustments(lineItem.priceAdjustments ?? []);
}
